import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Tooltip,
  TextField,
} from '@mui/material';
import { T, stitchInputSx, stitchTooltipSlotProps } from './stitch';


/**
 * Klik-om-te-bewerken tekstveld. Toont de waarde als gewone tekst; bij klikken
 * verschijnt een invoerveld dat opslaat bij blur of Enter en annuleert bij Escape.
 * Staat bewust op module-niveau: een component binnen een parent hermount bij elke
 * toetsaanslag (cursor springt naar het begin).
 */
function InlineTextEdit({
  value,
  onSave,
  placeholder = 'Klik om in te vullen',
  multiline = false,
  numeric = false,
  suffix = '',
  disabled = false,
  displaySx = {},
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const annuleren = useRef(false);

  const huidig = value === null || value === undefined ? '' : String(value);

  const start = () => {
    if (disabled) return;
    setDraft(huidig);
    annuleren.current = false;
    setEditing(true);
  };

  const commit = async () => {
    if (annuleren.current) {
      annuleren.current = false;
      setEditing(false);
      return;
    }
    setEditing(false);
    const schoon = draft.trim();
    if (schoon === huidig.trim()) return;
    if (numeric) {
      if (schoon === '') return onSave(null);
      const getal = Number(schoon.replace(',', '.'));
      if (!isFinite(getal)) {
        toast.error('Vul een getal in');
        return;
      }
      return onSave(getal);
    }
    return onSave(multiline ? draft : schoon);
  };

  if (editing) {
    return (
      <TextField
        autoFocus
        fullWidth
        size="small"
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            annuleren.current = true;
            setEditing(false);
          } else if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            e.target.blur();
          }
        }}
        sx={stitchInputSx}
      />
    );
  }

  return (
    <Tooltip title={disabled ? '' : 'Klik om te bewerken'} arrow slotProps={stitchTooltipSlotProps}>
      <Box
        onClick={start}
        sx={{
          fontFamily: T.fontBody,
          fontSize: '0.875rem',
          color: huidig ? T.text : T.outline,
          fontStyle: huidig ? 'normal' : 'italic',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          cursor: disabled ? 'default' : 'text',
          borderRadius: '0.5rem',
          px: 1,
          py: 0.5,
          mx: -1,
          minHeight: 28,
          transition: 'background-color 0.15s ease',
          '&:hover': disabled ? {} : { bgcolor: T.surfaceLow },
          ...displaySx,
        }}
      >
        {huidig ? `${huidig}${suffix}` : placeholder}
      </Box>
    </Tooltip>
  );
}

export default InlineTextEdit;
